import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { adminService } from '../../../services/adminService';

const UsageAnalytics = () => {
  const [timeRange, setTimeRange] = useState('30days');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await adminService?.getUsageAnalytics(timeRange);
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load analytics');
        return;
      }
      
      setAnalytics(data);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const timeRangeOptions = [
    { value: '7days', label: 'Last 7 Days' },
    { value: '30days', label: 'Last 30 Days' },
    { value: '90days', label: 'Last 90 Days' },
    { value: '1year', label: 'Last Year' }
  ];

  // Use real KPI data or fallback
  const kpiData = analytics?.kpis ? [
    {
      label: "Avg. Time to Booking",
      value: analytics?.kpis?.avgTimeToBooking || "N/A",
      change: "-12%",
      trend: "down",
      icon: "Clock",
      color: "text-success"
    },
    {
      label: "Match Acceptance Rate",
      value: analytics?.kpis?.matchAcceptanceRate || "0%",
      change: "+5.2%",
      trend: "up",
      icon: "UserCheck",
      color: "text-success"
    },
    {
      label: "Session Completion",
      value: analytics?.kpis?.sessionCompletion || "0%",
      change: "+2.1%",
      trend: "up",
      icon: "CheckCircle2",
      color: "text-success"
    },
    {
      label: "Active Users",
      value: analytics?.kpis?.activeUsers?.toString() || "0",
      change: "+18%",
      trend: "up",
      icon: "Users",
      color: "text-primary"
    }
  ] : [];

  // Use real booking trend data or fallback
  const bookingTrendData = analytics?.bookingTrends || [];

  // Use real helper type data or fallback
  const helperTypeData = analytics?.helperTypeDistribution || [];

  // Use real top issues data or fallback
  const topIssuesData = analytics?.topIssues || [];

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-12">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadAnalytics}
            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Icon name="BarChart3" size={24} className="text-primary" />
          <h3 className="text-lg font-semibold">Usage Analytics</h3>
        </div>
        <Select
          options={timeRangeOptions}
          value={timeRange}
          onChange={setTimeRange}
          className="w-full sm:w-48"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiData?.map((kpi, index) => (
          <div key={index} className="bg-card rounded-lg border border-border p-6">
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center bg-primary/10`}>
                <Icon name={kpi?.icon} size={24} className="text-primary" />
              </div>
              <span className={`text-sm font-medium ${kpi?.color}`}>
                {kpi?.change}
              </span>
            </div>
            <p className="text-2xl font-bold mb-1 text-data">{kpi?.value}</p>
            <p className="text-sm text-muted-foreground">{kpi?.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-6">
          <h4 className="text-base font-semibold mb-4">Booking Trends</h4>
          {bookingTrendData?.length > 0 ? (
            <div className="w-full h-64" aria-label="Booking trends line chart showing bookings and completions over time">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={bookingTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="date" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                  <YAxis stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                  <Line type="monotone" dataKey="bookings" stroke="#4A90A4" strokeWidth={2} name="Bookings" />
                  <Line type="monotone" dataKey="completions" stroke="#48BB78" strokeWidth={2} name="Completions" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
              <p>No booking data available for this period</p>
            </div>
          )}
        </div>

        <div className="bg-card rounded-lg border border-border p-6">
          <h4 className="text-base font-semibold mb-4">Helper Type Distribution</h4>
          {helperTypeData?.length > 0 ? (
            <div className="w-full h-64" aria-label="Pie chart showing distribution of helper types">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={helperTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {helperTypeData?.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'var(--color-popover)', 
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }} 
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
              <p>No helper data available</p>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-6">
        <h4 className="text-base font-semibold mb-4">Top Issues Addressed</h4>
        {topIssuesData?.length > 0 ? (
          <div className="w-full h-80" aria-label="Bar chart showing top issues addressed in sessions">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topIssuesData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis type="number" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} />
                <YAxis dataKey="issue" type="category" stroke="var(--color-muted-foreground)" style={{ fontSize: '12px' }} width={100} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }} 
                />
                <Bar dataKey="sessions" fill="#4A90A4" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="w-full h-80 flex items-center justify-center text-muted-foreground">
            <p>No issue data available for this period</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageAnalytics;